import type {
	RelationOutputForBuyer,
	RelationOutputForSeller,
} from "@/api/custom_types";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import RelationNameLink from "@/pages/relations/RelationNameLink";
import { useCreatePayment } from "@/services";
import { formatNumber } from "@/utils/tools";
import type { TranslationF } from "@/utils/type-utils";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	Button,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogScrollableArea,
	DialogTitle,
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Icon,
	Input,
	Typography,
	toast,
} from "@st-marketplace/ui";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

interface BalanceAdjustmentDialogProps {
	relation: RelationOutputForBuyer | RelationOutputForSeller;
	open: boolean;
	onClose: () => void;
}

const getAdjustmentSchema = (t: TranslationF) =>
	z.object({
		amount: z.coerce.number().positive(t("payment.adjustment.amountInvalid")),
		direction: z.enum(["increase_debt", "decrease_debt"]),
		payment_note: z.string().trim().min(1, t("validation.required")),
	});

type AdjustmentFormData = z.infer<ReturnType<typeof getAdjustmentSchema>>;

const BalanceAdjustmentDialog = ({
	relation,
	open,
	onClose,
}: BalanceAdjustmentDialogProps) => {
	const { t } = useTranslation();
	const createPaymentMutation = useCreatePayment();

	const form = useForm<AdjustmentFormData>({
		resolver: zodResolver(getAdjustmentSchema(t)),
		defaultValues: {
			amount: 0,
			direction: "increase_debt",
			payment_note: "",
		},
	});

	const watchedValues = form.watch();
	const parsedCurrentDebt = Number(relation.current_debt);
	const currentDebt = Number.isFinite(parsedCurrentDebt)
		? parsedCurrentDebt
		: 0;
	const amount = Number(watchedValues.amount) || 0;
	const projectedDebt =
		watchedValues.direction === "increase_debt"
			? currentDebt + amount
			: currentDebt - amount;

	const resetForm = () => {
		form.reset({
			amount: 0,
			direction: "increase_debt",
			payment_note: "",
		});
	};

	const handleClose = () => {
		resetForm();
		onClose();
	};

	const [showConfirm, setShowConfirm] = useState(false);

	const directionLabel =
		watchedValues.direction === "increase_debt"
			? t("payment.adjustment.increase").toLowerCase()
			: t("payment.adjustment.decrease").toLowerCase();

	const onSubmitForm = () => {
		setShowConfirm(true);
	};

	const handleConfirm = form.handleSubmit((data) => {
		createPaymentMutation.mutate(
			{
				body: {
					type: "ADJUSTMENT",
					relation_id: relation.id,
					amount: data.amount,
					direction: data.direction,
					payment_note: data.payment_note,
				},
			},
			{
				onSuccess: () => {
					// useCreatePayment already refreshes every balance-bearing list.
					toast.success(t("payment.adjustment.successMessage"));
					handleClose();
				},
				onError: () => setShowConfirm(false),
			},
		);
	});

	return (
		<>
			<Dialog
				open={open && !showConfirm}
				onOpenChange={(o) => !o && handleClose()}
			>
				<DialogContent className="w-full max-w-xl">
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2">
							<Icon name="wallet-line" className="text-primary-100" />
							{t("payment.adjustment.dialogTitle")} ·{" "}
							<RelationNameLink
								name={relation.buyer_display_name}
								relationId={relation.id}
								party="buyer"
							/>
						</DialogTitle>
						<DialogDescription>
							<Typography variant="body-sm" className="text-start">
								{t("payment.adjustment.helperText")}
							</Typography>
						</DialogDescription>
					</DialogHeader>

					<DialogScrollableArea className="max-h-[90vh] pt-0 pb-6">
						<Form {...form}>
							<form
								noValidate
								onSubmit={form.handleSubmit(onSubmitForm)}
								className="flex flex-col gap-6"
							>
								<div className="flex items-center justify-between gap-4 rounded-xl border border-secondary-border bg-background-100 p-4">
									<div>
										<Typography
											variant="caption"
											className="text-secondary-text-100"
										>
											{t("payment.adjustment.currentBalance")}
										</Typography>
										<Typography variant="title-sm" className="mt-1">
											{formatNumber(currentDebt, "price")}
										</Typography>
									</div>
									<Icon
										name="money-dollar-circle-line"
										size="lg"
										className="text-secondary-icon"
									/>
								</div>

								<FormField
									control={form.control}
									name="direction"
									render={({ field }) => (
										<FormItem>
											<FormLabel>{t("payment.adjustment.direction")}</FormLabel>
											<div
												role="radiogroup"
												className="grid grid-cols-2 gap-2 rounded-xl bg-background-300 p-1"
											>
												{(
													[
														["increase_debt", "add-line", "increase"],
														["decrease_debt", "subtract-line", "decrease"],
													] as const
												).map(([value, icon, label]) => {
													const active = field.value === value;
													return (
														<button
															key={value}
															type="button"
															role="radio"
															aria-checked={active}
															onClick={() => field.onChange(value)}
															className={cn(
																"flex h-11 items-center justify-center gap-2 rounded-lg font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-border",
																active
																	? "bg-primary-fill text-white shadow-sm"
																	: "text-secondary-text-100 hover:bg-background-100",
															)}
														>
															<Icon name={icon} size="sm" />
															{t(`payment.adjustment.${label}`)}
														</button>
													);
												})}
											</div>
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="amount"
									render={({ field }) => (
										<FormItem>
											<FormLabel required>
												{t("payment.adjustment.amount")}
											</FormLabel>
											<FormControl>
												<Input
													type="number"
													placeholder={t("payment.adjustment.amount")}
													value={field.value ?? ""}
													onChange={field.onChange}
													min={0}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="payment_note"
									render={({ field }) => (
										<FormItem>
											<FormLabel required>
												{t("payment.adjustment.reason")}
											</FormLabel>
											<FormControl>
												<textarea
													{...field}
													rows={4}
													placeholder={t("payment.adjustment.reason")}
													className="w-full resize-none rounded-lg border border-secondary-border bg-transparent px-3 py-2 outline-none placeholder:text-secondary-text-disabled focus:border-primary-border"
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								<div className="flex items-center justify-between gap-4 rounded-xl border border-secondary-border bg-background-100 p-4">
									<Typography
										variant="body-sm"
										className="text-secondary-text-100"
									>
										{t("payment.adjustment.newBalance")}
									</Typography>
									<Typography
										variant="title-sm"
										className={cn(
											"tabular-nums",
											watchedValues.direction === "decrease_debt"
												? "text-success-text"
												: "text-danger-text",
										)}
									>
										{formatNumber(projectedDebt, "price")}
									</Typography>
								</div>

								<div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
									<Button type="button" color="secondary" onClick={handleClose}>
										{t("common.button.cancel")}
									</Button>
									<Button type="submit" color="primary">
										{t("payment.adjustment.buttonText")}
									</Button>
								</div>
							</form>
						</Form>
					</DialogScrollableArea>
				</DialogContent>
			</Dialog>

			<Dialog open={showConfirm} onOpenChange={setShowConfirm}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t("payment.adjustment.confirmTitle")}</DialogTitle>
					</DialogHeader>
					<div className="px-4">
						<Typography className="text-start">
							{t("payment.adjustment.confirmMessage", {
								direction: directionLabel,
								buyer: relation.buyer_display_name,
								amount: formatNumber(watchedValues.amount, "price"),
							})}
						</Typography>
					</div>
					<DialogFooter
						buttons={{
							secondary: {
								label: t("common.button.no"),
								props: {
									type: "button",
									onClick: () => setShowConfirm(false),
								},
							},
							primary: {
								label: t("action.button.confirmAccept"),
								props: {
									type: "button",
									loading: createPaymentMutation.isPending,
									onClick: handleConfirm,
								},
							},
						}}
					/>
				</DialogContent>
			</Dialog>
		</>
	);
};

export default BalanceAdjustmentDialog;
